import React, { SyntheticEvent, useMemo } from 'react';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { useLocalizedJobNames } from 'src/features/jobs';
import { useTimesheetData, useTimesheetQuery } from 'src/features/timesheets/providers';
import { Switch } from 'src/shared/components';
import { DATE_MONTH_FORMAT } from 'src/shared/constants';
import { PermissionsEnums, TimesheetSummaryTypeEnums } from 'src/shared/enums';
import { ComponentWithPermissions } from 'src/shared/hocs';
import { useTranslate } from 'src/shared/providers';
import { formatDepartmentDisplay, formatInputDuration } from 'src/shared/utils';
import { RootState } from 'src/store/types';
import { getDurationFormat, getEmployeeDepartmentDisplayFormat } from 'src/store/user/user.selectors';

export const TimesheetSummary = () => {
  const { t } = useTranslate();
  const { settings } = useSelector((state: RootState) => state.user);
  const numberOfJobLevels = settings?.numberOfJobLevels;
  const localizedJobNames = useLocalizedJobNames(numberOfJobLevels);
  const durationFormat = useSelector(getDurationFormat);
  const employeeDepartmentDisplayFormat = useSelector(getEmployeeDepartmentDisplayFormat);

  const {
    selectedPayPeriodWeek,
    type,
    showDepartment,
    showJobs,
    showExpenses,
    onTypeChange,
    onShowDepartmentToggle,
    onShowJobsToggle,
    onShowExpensesToggle,
  } = useTimesheetQuery();
  const { timesheetSummary, loadingTimesheetSummary } = useTimesheetData();

  const handleTabChange = (_: SyntheticEvent, tab: TimesheetSummaryTypeEnums) => {
    onTypeChange(tab);
  };

  const totalPremiumAmount = useMemo(() => {
    return timesheetSummary?.rows.reduce((acc, row) => acc + (row.premiumAmount || 0), 0) ?? 0;
  }, [timesheetSummary?.rows]);

  return (
    <Paper sx={{ borderRadius: '8px', width: 'fit-content', minWidth: 600, padding: '16px 0' }}>
      <Stack gap={1}>
        <Typography fontSize="0.875rem" fontWeight={600} sx={{ padding: '0 16px' }}>
          {t('global.summary')}
        </Typography>
        <Divider />
        <Tabs
          value={type}
          onChange={handleTabChange}
          sx={{ '&, button': { height: '38px', minHeight: '38px', textTransform: 'none', padding: '0 16px' } }}
        >
          <Tab value={TimesheetSummaryTypeEnums.WEEKLY} label={t('timesheets.weeklyTotals')} />
          <Tab value={TimesheetSummaryTypeEnums.PAY_PERIOD} label={t('timesheets.payPeriod')} />
        </Tabs>
        {!selectedPayPeriodWeek || loadingTimesheetSummary || !timesheetSummary ? (
          <Skeleton
            className="rounded"
            variant="rectangular"
            animation="wave"
            height="120px"
            sx={{ margin: '0 16px' }}
          />
        ) : (
          <>
            <Stack direction="row" padding="0 32px" alignItems="center" gap={2}>
              <Typography fontSize="0.75rem" fontWeight={600} color={(theme) => theme.palette.primary.main}>
                {moment(timesheetSummary.startDate).format(DATE_MONTH_FORMAT)}
                {' - '}
                {moment(timesheetSummary.endDate).format(DATE_MONTH_FORMAT)}
              </Typography>
              <Typography fontSize="0.75rem" fontWeight={600}>
                {t('timesheets.breakdownBy')}:
              </Typography>
              <ComponentWithPermissions permissions={PermissionsEnums.FEATUREDEPARTMENT_ALLOCATION_FUNCTION}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography fontSize="0.75rem" color={(theme) => theme.palette.primary.main}>
                    {t('timesheets.departments')}
                  </Typography>
                  <Switch checked={showDepartment} onChange={onShowDepartmentToggle} />
                </Stack>
              </ComponentWithPermissions>
              <ComponentWithPermissions permissions={PermissionsEnums.FEATUREJOB_COSTING_FUNCTION}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography fontSize="0.75rem" color={(theme) => theme.palette.primary.main}>
                    {t('global.jobs')}
                  </Typography>
                  <Switch checked={showJobs} onChange={onShowJobsToggle} />
                </Stack>
              </ComponentWithPermissions>
              <ComponentWithPermissions permissions={PermissionsEnums.WTS_INPUT_EXPENSE}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography fontSize="0.75rem" color={(theme) => theme.palette.primary.main}>
                    {t('timesheets.expenses')}
                  </Typography>
                  <Switch checked={showExpenses} onChange={onShowExpensesToggle} />
                </Stack>
              </ComponentWithPermissions>
            </Stack>
            <TableContainer sx={{ padding: '0 32px' }}>
              <Table size="small">
                <TableHead
                  sx={{
                    th: {
                      color: (theme) => theme.palette.action.active,
                      fontWeight: 'normal',
                      border: 'none',
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  <TableRow>
                    <TableCell>{t('timesheets.timeCategory')}</TableCell>
                    <ComponentWithPermissions permissions={PermissionsEnums.FEATUREDEPARTMENT_ALLOCATION_FUNCTION}>
                      {showDepartment && <TableCell>{t('timesheets.department')}</TableCell>}
                    </ComponentWithPermissions>
                    <ComponentWithPermissions permissions={PermissionsEnums.FEATUREJOB_COSTING_FUNCTION}>
                      {showJobs &&
                        [...Array(numberOfJobLevels)].map((_, index) => (
                          <TableCell key={localizedJobNames[index]}>{localizedJobNames[index]}</TableCell>
                        ))}
                    </ComponentWithPermissions>
                    <TableCell>{t('timesheets.totalHours')}</TableCell>
                    {!!totalPremiumAmount && <TableCell>{t('timesheets.premium')}</TableCell>}
                    <ComponentWithPermissions permissions={PermissionsEnums.WTS_INPUT_EXPENSE}>
                      {showExpenses && <TableCell>{t('timesheets.expenses')}</TableCell>}
                    </ComponentWithPermissions>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ 'tr:not(:nth-last-child(2))': { td: { border: 'none' } } }}>
                  {timesheetSummary.rows.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.timeCategory?.description}</TableCell>
                      <ComponentWithPermissions permissions={PermissionsEnums.FEATUREDEPARTMENT_ALLOCATION_FUNCTION}>
                        {showDepartment && (
                          <TableCell>
                            {row.department &&
                              formatDepartmentDisplay(
                                row.department.description,
                                row.department.number,
                                employeeDepartmentDisplayFormat,
                              )}
                          </TableCell>
                        )}
                      </ComponentWithPermissions>
                      <ComponentWithPermissions permissions={PermissionsEnums.FEATUREJOB_COSTING_FUNCTION}>
                        {showJobs &&
                          [...Array(numberOfJobLevels)].map((_, index) => (
                            <TableCell key={localizedJobNames[index]}>
                              {row.jobs[index]?.values[0] &&
                                row.jobs[index]?.values[0]?.value + ' - ' + row.jobs[index]?.values[0]?.description}
                            </TableCell>
                          ))}
                      </ComponentWithPermissions>
                      <TableCell>
                        {row.totalDurationInSeconds &&
                          formatInputDuration(row.totalDurationInSeconds, durationFormat, 'seconds', false)}
                      </TableCell>
                      <TableCell>
                        {row.premiumAmount && formatInputDuration(row.premiumAmount, durationFormat, 'seconds', false)}
                      </TableCell>
                      <ComponentWithPermissions permissions={PermissionsEnums.WTS_INPUT_EXPENSE}>
                        {showExpenses && <TableCell>{row.totalExpenses?.toFixed(2)}</TableCell>}
                      </ComponentWithPermissions>
                    </TableRow>
                  ))}
                  <TableRow sx={{ td: { fontWeight: 600 } }}>
                    <TableCell />
                    <ComponentWithPermissions permissions={PermissionsEnums.FEATUREDEPARTMENT_ALLOCATION_FUNCTION}>
                      {showDepartment && <TableCell />}
                    </ComponentWithPermissions>
                    <ComponentWithPermissions permissions={PermissionsEnums.FEATUREJOB_COSTING_FUNCTION}>
                      {showJobs && <TableCell colSpan={numberOfJobLevels} />}
                    </ComponentWithPermissions>
                    <TableCell>
                      {formatInputDuration(timesheetSummary.totalDurationInSeconds, durationFormat, 'seconds', false)}
                    </TableCell>
                    {!!totalPremiumAmount && (
                      <TableCell>{formatInputDuration(totalPremiumAmount, durationFormat, 'seconds', false)}</TableCell>
                    )}
                    <ComponentWithPermissions permissions={PermissionsEnums.WTS_INPUT_EXPENSE}>
                      {showExpenses && <TableCell>{timesheetSummary.totalExpensesAmount.toFixed(2)}</TableCell>}
                    </ComponentWithPermissions>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Stack>
    </Paper>
  );
};
